import React, { useState, useRef } from "react";
import { z } from "zod";

import { CindraButton } from "cindra/CindraButton";
import { getAuthToken } from "services/AccountServices";
import { getSelectedAgent } from "cindra/MastraClient";
import { useMCP } from "mcp/hooks/useMCP";
import { useNotificationManager } from "notifications/NotificationManager";

// Toggle between LLM-mediated and direct MCP tool call methods
// Set to true for direct tool calls (faster, cheaper, bypasses LLM)
// Set to false for LLM-mediated calls (uses agent to call MCP tool)
const USE_DIRECT_TOOL_CALL = false; // Default to LLM for better quality summary

// Debug logging flags
const ENABLE_DEBUG_LOGS = false;
const ENABLE_TIMING_LOGS = false;

// Structured output schema for LLM response
const SubjectiveProgressSummarySchema = z.object({
    summary: z.string().describe("The subjective progress summary text")
});

interface CindraSubjectiveProgressSummaryProps {
    patientFullName: string;
    onSummaryGenerated: (summary: string) => void;
    onGenerationStart?: () => void;
    onGenerationComplete?: () => void;
    maxCharacters?: number;
    isGenerator?: boolean;
}

export const CindraSubjectiveProgressSummary: React.FC<CindraSubjectiveProgressSummaryProps> = ({
    patientFullName,
    onSummaryGenerated,
    onGenerationStart,
    onGenerationComplete,
    maxCharacters = 2000,
    isGenerator = false
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const buttonRef = useRef<HTMLDivElement>(null);
    const notificationManager = useNotificationManager();
    const mcp = useMCP();

    // Extract summary from conversational text
    const extractSummaryFromConversationalText = (text: string): string => {
        // Common conversational patterns to strip
        const conversationalPatterns = [
            /^Here is (?:the )?(?:subjective progress )?summary(?: for patient [^:]+)?:\s*/i,
            /^Here's (?:the )?(?:subjective progress )?summary(?: for patient [^:]+)?:\s*/i,
            /^(?:I've generated|I found) (?:the )?(?:subjective progress )?summary(?: for patient [^:]+)?:\s*/i,
            /^(?:The )?(?:Subjective progress )?(?:summary)(?: for [^:]+)?:\s*/i,
        ];

        let cleanedText = text;

        // Try to remove conversational prefix
        for (const pattern of conversationalPatterns) {
            cleanedText = cleanedText.replace(pattern, '');
        }

        // Remove any trailing conversational text
        const trailingPatterns = [
            /\n+If you (?:need|have) (?:further|any) (?:details|questions)[^\n]*$/i,
            /\n+Feel free to (?:ask|let me know)[^\n]*$/i,
            /\n+Let me know if you (?:need|have)[^\n]*$/i,
        ];

        for (const pattern of trailingPatterns) {
            cleanedText = cleanedText.replace(pattern, '');
        }

        return cleanedText.trim();
    };

    // Fetch subjective progress summary using the LLM-mediated approach
    const fetchSummaryViaLLM = async (): Promise<string> => {
        if (ENABLE_TIMING_LOGS) console.log('[CindraSubjectiveProgressSummary] [TIMING] Starting LLM-mediated fetch');
        const startTime = ENABLE_TIMING_LOGS ? performance.now() : 0;

        const authToken = getAuthToken();

        // Get the currently selected agent based on cookie preference
        const selectedAgent = getSelectedAgent();

        // Build an optimized prompt that encourages the agent to use the get_patient_outcomes tool
        const prompt = `[auth_token: ${authToken}]

Use the get_patient_outcomes tool with full_name="${patientFullName}", subjective=true, and objective=false to retrieve subjective outcomes data.

Generate a comprehensive, professionally formatted subjective progress summary based on this data. The summary should:
- Focus on the patient's engagement with the program and improvements in their life
- Emphasize the patient's own perspective and progress they have communicated
- Highlight areas of success and those that need improvement
- Provide clinical significance where possible
- Include prognosis and recommendations where appropriate
- Be concise and limited to approximately ${maxCharacters} characters
- Use a professional clinical tone suitable for a medical report
- Structure the summary as multiple distinct paragraphs (3-4 paragraphs recommended)
- Do NOT include a title, patient name header, or section headers with colons (like "Patient Engagement:", "Areas of Success:", etc.)
- Do NOT use markdown formatting, bold text, or bullet points
- Separate paragraphs with blank lines (using \\n\\n)
- IMPORTANT: Use the correct gender pronouns based on the gender field of the outcomes data. If the patient's gender is female, use she/her/hers pronouns. If male, use he/him/his pronouns. If gender is not specified or is non-binary, use they/them/their pronouns.

Return ONLY the plain text summary organized into multiple paragraphs without any conversational preamble, explanations, titles, or headers.`;

        // Call the selected agent with the prompt
        let result: { text?: string; object?: any } | null;

        const agentCallStart = ENABLE_TIMING_LOGS ? performance.now() : 0;
        if (typeof selectedAgent.generateVNext === "function") {
            result = await selectedAgent.generateVNext(
                prompt,
                {
                    maxSteps: 3
                }
            );
        }
        else {
            result = await selectedAgent.generate(
                {
                    messages: [{ role: "user", content: prompt }],
                    maxSteps: 3
                }
            );
        }

        if (ENABLE_TIMING_LOGS) {
            const agentCallTime = performance.now() - agentCallStart;
            console.log('[CindraSubjectiveProgressSummary] [TIMING] Agent call took', agentCallTime, 'ms');
        }

        if (ENABLE_DEBUG_LOGS) {
            console.log('[CindraSubjectiveProgressSummary] Full result:', result);
            console.log('[CindraSubjectiveProgressSummary] Result keys:', result ? Object.keys(result) : 'null');
            console.log('[CindraSubjectiveProgressSummary] result.text:', result?.text);
        }

        // Try to get the summary directly from tool results first (fastest path)
        const toolResults = (result as any)?.toolResults;
        if (toolResults && toolResults.length > 0) {
            const toolResult = toolResults[0];
            // Check for summary in the structured response
            const summary = toolResult?.payload?.result?.structuredContent?.summary;
            if (summary) {
                if (ENABLE_TIMING_LOGS) console.log('[CindraSubjectiveProgressSummary] [TIMING] Total LLM fetch took', performance.now() - startTime, 'ms');
                if (ENABLE_DEBUG_LOGS) console.log('[CindraSubjectiveProgressSummary] Found summary directly in tool results');
                return summary;
            }
        }

        // Fallback to text response from LLM
        if (result?.text) {
            if (ENABLE_DEBUG_LOGS) console.log('[CindraSubjectiveProgressSummary] Got response with text');

            // Try to parse as JSON first
            try {
                const parsed = JSON.parse(result.text);
                if (parsed.summary) {
                    if (ENABLE_DEBUG_LOGS) console.log('[CindraSubjectiveProgressSummary] Found summary in parsed JSON');
                    if (ENABLE_TIMING_LOGS) console.log('[CindraSubjectiveProgressSummary] [TIMING] Total LLM fetch took', performance.now() - startTime, 'ms');
                    return parsed.summary;
                }
                else if (parsed.error) {
                    throw new Error(parsed.error);
                }
            }
            catch (parseError) {
                // JSON parsing failed - LLM returned plain text
                if (ENABLE_DEBUG_LOGS) console.log('[CindraSubjectiveProgressSummary] Not JSON, treating as summary directly');
            }

            // Use the response directly as summary
            const text = result.text.trim();
            if (text) {
                const cleanedSummary = extractSummaryFromConversationalText(text);
                if (ENABLE_TIMING_LOGS) console.log('[CindraSubjectiveProgressSummary] [TIMING] Total LLM fetch took', performance.now() - startTime, 'ms');
                if (ENABLE_DEBUG_LOGS) console.log('[CindraSubjectiveProgressSummary] Using extracted summary');
                return cleanedSummary;
            }
            else {
                throw new Error("Empty response from agent");
            }
        }
        else {
            throw new Error("No response generated from agent");
        }
    };

    // Fetch subjective progress summary using direct MCP tool call
    const fetchSummaryDirectly = async (): Promise<string> => {
        const authToken = getAuthToken();

        if (ENABLE_TIMING_LOGS) console.log('[CindraSubjectiveProgressSummary] [TIMING] Starting direct MCP tool call');
        const startTime = ENABLE_TIMING_LOGS ? performance.now() : 0;

        // Call the MCP tool directly using the useMCP hook
        const result = await mcp.callTool('get_patient_outcomes', {
            auth_token: authToken,
            full_name: patientFullName,
            subjective: true,
            objective: false
        });

        const mcpCallTime = ENABLE_TIMING_LOGS ? performance.now() : 0;
        if (ENABLE_TIMING_LOGS) console.log('[CindraSubjectiveProgressSummary] [TIMING] MCP tool call completed in', mcpCallTime - startTime, 'ms');
        if (ENABLE_DEBUG_LOGS) console.log('[CindraSubjectiveProgressSummary] Direct MCP tool call result:', result);

        // Extract content from the MCP response
        if (result.content && Array.isArray(result.content)) {
            for (const item of result.content) {
                if (item.type === 'text' && item.text) {
                    const jsonParseStart = ENABLE_TIMING_LOGS ? performance.now() : 0;

                    try {
                        const response = JSON.parse(item.text);
                        if (ENABLE_TIMING_LOGS) console.log('[CindraSubjectiveProgressSummary] [TIMING] JSON parse took', performance.now() - jsonParseStart, 'ms');

                        // The direct tool call returns raw data, not a summary
                        // We need to format this data into a summary
                        // For now, return an error indicating LLM is required
                        throw new Error("Direct MCP call requires LLM processing. Please use LLM-mediated mode.");
                    } catch (parseError) {
                        console.error('[CindraSubjectiveProgressSummary] Failed to parse MCP response:', parseError);
                        throw parseError;
                    }
                }
            }
        }

        throw new Error("Invalid response from MCP tool - no text content found");
    };

    const handleButtonClick = async (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent any parent click handlers

        if (ENABLE_TIMING_LOGS) console.log('[CindraSubjectiveProgressSummary] [TIMING] Button clicked, starting fetch');
        const overallStartTime = ENABLE_TIMING_LOGS ? performance.now() : 0;

        setIsLoading(true);

        // Notify parent that generation has started (to disable textarea and clear content)
        if (onGenerationStart) {
            onGenerationStart();
        }

        // Clear the textarea content immediately
        onSummaryGenerated("");

        try {
            // Fetch summary using the selected method
            const fetchStartTime = ENABLE_TIMING_LOGS ? performance.now() : 0;
            const summary = USE_DIRECT_TOOL_CALL
                ? await fetchSummaryDirectly()
                : await fetchSummaryViaLLM();

            const fetchEndTime = ENABLE_TIMING_LOGS ? performance.now() : 0;
            if (ENABLE_TIMING_LOGS) console.log('[CindraSubjectiveProgressSummary] [TIMING] Fetch completed in', fetchEndTime - fetchStartTime, 'ms');

            // Truncate if necessary, but ensure we end with a complete sentence
            let truncatedSummary = summary;
            if (summary.length > maxCharacters) {
                // Find the last sentence-ending punctuation before the character limit
                const truncated = summary.substring(0, maxCharacters);
                const sentenceEndings = /[.!?]\s/g;
                let lastMatch;
                let match;

                while ((match = sentenceEndings.exec(truncated)) !== null) {
                    lastMatch = match;
                }

                if (lastMatch) {
                    // Include the punctuation mark but not the trailing space
                    truncatedSummary = summary.substring(0, lastMatch.index + 1);
                } else {
                    // If no sentence ending found, just use the truncated version
                    truncatedSummary = truncated;
                }
            }

            // Call the callback to populate the textarea
            onSummaryGenerated(truncatedSummary);

            if (ENABLE_TIMING_LOGS) console.log('[CindraSubjectiveProgressSummary] [TIMING] Total handleButtonClick took', performance.now() - overallStartTime, 'ms');
        }
        catch (error) {
            console.error("[CindraSubjectiveProgressSummary] Error fetching subjective progress summary:", error);

            // Determine error message
            let errorMessage = "Failed to generate subjective progress summary. ";
            if (error instanceof Error) {
                const lowerMessage = error.message.toLowerCase();
                if (lowerMessage.includes("api key") || lowerMessage.includes("unauthorized")) {
                    errorMessage += "API key configuration issue.";
                }
                else if (lowerMessage.includes("network") || lowerMessage.includes("connection")) {
                    errorMessage += "Network connection issue.";
                }
                else if (lowerMessage.includes("token") || lowerMessage.includes("auth")) {
                    errorMessage += "Authentication issue.";
                }
                else {
                    errorMessage += error.message;
                }
            }
            else {
                errorMessage += "Unknown error occurred.";
            }

            notificationManager.addError(errorMessage, { duration: 5 });
        }
        finally {
            setIsLoading(false);

            // Notify parent that generation has completed (to re-enable textarea)
            if (onGenerationComplete) {
                onGenerationComplete();
            }
        }
    };

    // Only render the button if isGenerator is true
    if (!isGenerator) {
        return null;
    }

    // Cindra button icon (lightning bolt)
    const cindraIcon = (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
    );

    return (
        <CindraButton
            color="blue"
            icon={cindraIcon}
            isLoading={isLoading}
            ref={buttonRef}
            statusText="Generating..."
            style="normal"
            title="Generate subjective progress summary with Cindra"
            label="Generate Summary"
            minWidth={160}
            useFadeAnimation={false}
            // Callbacks
            onClick={handleButtonClick}
        />
    );
};
